import { FormEvent, useCallback, useEffect, useState } from "react";
import { SecretEntry, api, isError } from "../api";

export function CredentialsPage() {
  const [secrets, setSecrets] = useState<SecretEntry[] | null>(null);
  const [encryptionConfigured, setEncryptionConfigured] = useState<boolean>(
    true,
  );
  const [loadError, setLoadError] = useState<string | null>(null);
  const [message, setMessage] = useState<{
    text: string;
    tone: "ok" | "error";
  } | null>(null);

  const refresh = useCallback(async () => {
    const r = await api.listSecrets();
    if (isError(r)) {
      setLoadError(r.error);
      return;
    }
    setLoadError(null);
    setSecrets(r.secrets);
    setEncryptionConfigured(r.encryption_configured);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const groups: { name: string; entries: SecretEntry[] }[] = [];
  if (secrets != null) {
    for (const s of secrets) {
      // "System" secrets (webhook secret, bridge token) are generated by
      // the backend and revealed/rotated on their own pages — a free-text
      // "set value" box here would only invite typos.
      if (s.group === "System") continue;
      let bucket = groups.find((g) => g.name === s.group);
      if (!bucket) {
        bucket = { name: s.group, entries: [] };
        groups.push(bucket);
      }
      bucket.entries.push(s);
    }
  }

  async function save(key: string, label: string, value: string) {
    setMessage(null);
    const r = await api.setSecret(key, value);
    if (isError(r)) {
      setMessage({ text: `${label}: ${r.error}`, tone: "error" });
      void refresh();
      return;
    }
    setSecrets(r.secrets);
    setMessage({ text: `Saved ${label}.`, tone: "ok" });
  }

  async function clearSecret(key: string, label: string) {
    if (
      !window.confirm(
        `Clear the saved ${label}? The channel will fall back to the env value (or be disabled if none).`,
      )
    ) {
      return;
    }
    setMessage(null);
    const r = await api.clearSecret(key);
    if (isError(r)) {
      setMessage({ text: `${label}: ${r.error}`, tone: "error" });
      void refresh();
      return;
    }
    setSecrets(r.secrets);
    setMessage({ text: `Cleared ${label}.`, tone: "ok" });
  }

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-baseline justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Credentials</h1>
          <p className="text-xs text-fg-muted">
            sensitive secrets — encrypted at rest, never returned in
            plaintext over the API
          </p>
        </div>
      </header>

      {!encryptionConfigured && (
        <p className="rounded-md border border-warn/40 bg-warn/10 px-3 py-2 text-sm text-warn">
          <strong>ACCOUNT_ENCRYPTION_KEY is not set in env.</strong> The
          UI can read existing env-fallback values, but new secrets
          can&apos;t be saved until the key is configured. Generate one
          with{" "}
          <code className="rounded bg-page px-1 py-0.5 font-mono text-xs text-warn">
            python -c &quot;from cryptography.fernet import Fernet; print(Fernet.generate_key().decode())&quot;
          </code>{" "}
          and set <code className="font-mono">ACCOUNT_ENCRYPTION_KEY</code> in Coolify.
        </p>
      )}

      {loadError && (
        <p className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
          Couldn&apos;t load credentials: {loadError}
        </p>
      )}
      {message && (
        <p
          className={`rounded-md px-3 py-2 text-sm ${
            message.tone === "ok"
              ? "border border-success/40 bg-success/10 text-success"
              : "border border-danger/40 bg-danger/10 text-danger"
          }`}
        >
          {message.text}
        </p>
      )}

      {secrets === null && !loadError ? (
        <p className="text-sm text-fg-muted">Loading…</p>
      ) : (
        <div className="flex flex-col gap-4">
          {groups.map((group) => (
            <section
              key={group.name}
              className="rounded-xl border border-border bg-surface p-5"
            >
              <h2 className="mb-4 font-mono text-sm font-semibold uppercase tracking-wider text-fg-muted">
                {group.name}
              </h2>
              <ul className="space-y-5">
                {group.entries.map((entry) => (
                  <SecretRow
                    key={entry.key}
                    entry={entry}
                    onSave={(v) => save(entry.key, entry.label, v)}
                    onClear={() => clearSecret(entry.key, entry.label)}
                    canSave={encryptionConfigured}
                  />
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- row -------------------------------------------------------------

interface RowProps {
  entry: SecretEntry;
  onSave: (value: string) => Promise<void>;
  onClear: () => Promise<void>;
  canSave: boolean;
}

function SecretRow({ entry, onSave, onClear, canSave }: RowProps) {
  const [draft, setDraft] = useState("");
  const [reveal, setReveal] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!draft) return;
    setBusy(true);
    try {
      await onSave(draft);
      setDraft("");
      setReveal(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <li>
      <div className="flex items-baseline gap-2">
        <label
          htmlFor={`secret-${entry.key}`}
          className="text-sm font-medium text-fg"
        >
          {entry.label}
        </label>
        {entry.is_user_set ? (
          <span
            className="rounded bg-success/20 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-success"
            title="A user-managed value is set"
          >
            saved
          </span>
        ) : entry.has_env_fallback ? (
          <span
            className="rounded bg-warn/20 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-warn"
            title="Value comes from the env var; override here to manage from UI"
          >
            from env
          </span>
        ) : (
          <span
            className="rounded bg-bg-hover px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-fg-muted"
            title="No value configured — the consuming feature stays off"
          >
            not set
          </span>
        )}
      </div>
      <p className="mt-0.5 text-xs leading-relaxed text-fg-muted">
        {entry.description}
      </p>
      <form onSubmit={submit} className="mt-2 flex items-center gap-2">
        <input
          id={`secret-${entry.key}`}
          type={reveal ? "text" : "password"}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={entry.placeholder || "Paste new value"}
          autoComplete="off"
          className="min-w-0 flex-1 rounded-md border border-border bg-page px-2 py-1 font-mono text-sm text-fg outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
          disabled={busy || !canSave}
        />
        <button
          type="button"
          onClick={() => setReveal((v) => !v)}
          title={reveal ? "Hide" : "Show"}
          className="rounded-md border border-border px-2 py-1 text-[10px] uppercase tracking-wide text-fg-muted hover:bg-bg-hover hover:text-fg"
          disabled={busy}
        >
          {reveal ? "hide" : "show"}
        </button>
        <button
          type="submit"
          disabled={busy || !draft || !canSave}
          className="rounded-md bg-primary px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy ? "saving…" : "save"}
        </button>
        {entry.is_user_set && (
          <button
            type="button"
            onClick={() => void onClear()}
            title="Delete the saved value; falls back to env or 'not configured'"
            className="rounded-md border border-border px-2 py-1 text-[10px] uppercase tracking-wide text-fg-muted hover:bg-danger/10 hover:text-danger"
            disabled={busy}
          >
            clear
          </button>
        )}
      </form>
    </li>
  );
}
